import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/lib/api';
import { useCart } from '@/contexts/CartContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useStoreConfig } from '@/contexts/StoreConfigContext';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/storefront/Header';
import Footer from '@/components/storefront/Footer';
import ProductCard from '@/components/storefront/ProductCard';
import BundleWidget from '@/components/storefront/BundleWidget';
import PaymentIcons from '@/components/storefront/PaymentIcons';
import { ChevronLeft, ChevronRight, Minus, Plus, Truck, RotateCcw, ShieldCheck, ChevronDown, ShoppingBag, Loader2, Check, Award } from 'lucide-react';

function AccordionItem({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4"
        data-testid={`accordion-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <span className="font-sans text-sm font-bold uppercase tracking-wider" style={{ color: '#000' }}>{title}</span>
        <ChevronDown size={18} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} style={{ color: '#666' }} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="pb-5 font-sans text-sm leading-relaxed" style={{ color: '#555' }}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TrustBadge({ icon: Icon, title, desc, bgColor }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bgColor }}>
        <Icon size={18} strokeWidth={1.8} style={{ color: '#fff' }} />
      </div>
      <div>
        <p className="font-sans text-xs font-bold" style={{ color: '#000' }}>{title}</p>
        <p className="font-sans text-[11px]" style={{ color: '#888' }}>{desc}</p>
      </div>
    </div>
  );
}

export default function ProductDetailPage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { formatPrice } = useCurrency();
  const { config } = useStoreConfig();
  const storeName = config?.store_name || 'Florashop';
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [qtyBreaks, setQtyBreaks] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [optionErrors, setOptionErrors] = useState({});
  const [shakeBtn, setShakeBtn] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [showSticky, setShowSticky] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const addToCartRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setActiveImage(0);
    setQuantity(1);
    setJustAdded(false);
    setSelectedOptions({});
    setOptionErrors({});
    api.get(`/products/${handle}`).then(r => {
      setProduct(r.data);
      // Do NOT auto-select variant — user must choose
      if (r.data.variants?.length > 0 && (!r.data.options || r.data.options.length === 0)) {
        setSelectedVariant(r.data.variants[0]);
      }
      setLoading(false);
      if (r.data.category) {
        api.get(`/products?category=${r.data.category}&limit=5`).then(rel => {
          setRelated((rel.data.products || []).filter(p => p.handle !== handle).slice(0, 4));
        }).catch(() => {});
      }
    }).catch(() => setLoading(false));
    api.get(`/bundles?status=active&product_handle=${handle}`).then(r => {
      const all = r.data || [];
      setBundles(all.filter(b => b.type !== 'quantity_break'));
      setQtyBreaks(all.filter(b => b.type === 'quantity_break'));
    }).catch(() => {});
  }, [handle]);

  // IntersectionObserver for sticky bar (FIX 1)
  useEffect(() => {
    if (!addToCartRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(addToCartRef.current);
    return () => observer.disconnect();
  }, [product]);

  const scrollToReviews = useCallback(() => {
    document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const hasVariantOptions = product?.options?.length > 0 && product?.variants?.length > 0;
  const allOptionsSelected = hasVariantOptions ? product.options.every(opt => selectedOptions[opt.name]) : true;

  const handleAddToCart = async () => {
    if (addingToCart || justAdded) return;

    // Validate variant selection
    if (hasVariantOptions && !allOptionsSelected) {
      const errors = {};
      product.options.forEach(opt => {
        if (!selectedOptions[opt.name]) errors[opt.name] = true;
      });
      setOptionErrors(errors);
      setShakeBtn(true);
      setTimeout(() => setShakeBtn(false), 500);
      // Scroll to variant selectors
      const el = document.querySelector('[data-testid="product-title"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    setAddingToCart(true);
    await addItem(product, selectedVariant, quantity);
    setAddingToCart(false);
    setJustAdded(true);
    setTimeout(() => {
      setJustAdded(false);
      navigate('/checkout');
    }, 800);
  };

  if (loading) return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Header />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-square animate-pulse" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }} />
          <div className="space-y-4">
            <div className="h-6 w-2/3 animate-pulse rounded" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }} />
            <div className="h-8 w-1/3 animate-pulse rounded" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }} />
          </div>
        </div>
      </div>
    </div>
  );

  if (!product) return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-20 text-center">
        <p className="font-sans text-lg" style={{ color: '#999' }}>Product not found</p>
        <Link to="/shop" className="font-sans text-sm mt-4 inline-block" style={{ color: '#E8545A' }}>Back to Shop</Link>
      </div>
    </div>
  );

  const currentPrice = selectedVariant?.price || product.price;
  const comparePrice = selectedVariant?.compare_at_price || product.compare_at_price;
  const hasSale = comparePrice > currentPrice;
  const savePct = hasSale ? Math.round((1 - currentPrice / comparePrice) * 100) : 0;
  const images = product.images || [];
  const inStock = product.stock_quantity > 0;
  const lowStock = product.stock_quantity > 0 && product.stock_quantity < 10;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Product",
    "name": product.title,
    "description": product.description?.replace(/<[^>]+>/g, '').slice(0, 500),
    "image": images.map(i => i.url),
    "brand": { "@type": "Brand", "name": product.brand || storeName },
    "sku": selectedVariant?.sku || product.sku || product.handle,
    "mpn": product.sku || product.handle,
    "url": `${window.location.origin}/product/${product.handle}`,
    "offers": { "@type": "Offer", "price": currentPrice, "priceCurrency": "USD",
      "availability": inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "itemCondition": "https://schema.org/NewCondition",
      "url": `${window.location.origin}/product/${product.handle}`,
      "seller": { "@type": "Organization", "name": storeName },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": { "@type": "MonetaryAmount", "value": 0, "currency": "USD" },
        "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "US" },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 5, "unitCode": "DAY" },
          "transitTime": { "@type": "QuantitativeValue", "minValue": 3, "maxValue": 10, "unitCode": "DAY" }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": "US",
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 30,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn"
      }
    },
  };

  return (
    <div data-testid="product-detail-page" className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Header />
      <Helmet>
        <title>{`${product.title} — ${storeName}`}</title>
        <meta name="description" content={product.seo_description || product.description?.replace(/<[^>]+>/g, '').slice(0, 160)} />
        <meta property="og:title" content={product.title} />
        <meta property="og:description" content={product.seo_description || product.description?.replace(/<[^>]+>/g, '').slice(0, 160)} />
        <meta property="og:image" content={images[0]?.url || ''} />
        <meta property="og:type" content="product" />
        <meta property="product:price:amount" content={String(currentPrice)} />
        <meta property="product:price:currency" content="USD" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">
        <nav className="font-sans text-xs" style={{ color: '#999' }}>
          <Link to="/" className="hover:text-[#000]">Home</Link>
          <span className="mx-1.5">/</span>
          <Link to="/shop" className="hover:text-[#000]">Products</Link>
          <span className="mx-1.5">/</span>
          <span style={{ color: '#333' }}>{product.title}</span>
        </nav>
      </div>

      {/* Main */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
          {/* Gallery */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
            <div className="relative aspect-square overflow-hidden rounded-lg" style={{ backgroundColor: '#f5f3ef' }}>
              <AnimatePresence mode="wait">
                {images[activeImage] && (
                  <motion.img key={activeImage} src={images[activeImage].url} alt={product.title}
                    className="w-full h-full object-cover" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} />
                )}
              </AnimatePresence>
              {hasSale && (
                <span className="absolute top-4 left-4 px-3 py-1.5 font-sans text-xs font-bold rounded"
                  style={{ backgroundColor: '#000', color: '#fff' }}>Save {savePct}%</span>
              )}
              {images.length > 1 && (
                <>
                  <button data-testid="gallery-prev" onClick={() => setActiveImage(i => (i - 1 + images.length) % images.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full shadow"
                    style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}><ChevronLeft size={18} /></button>
                  <button data-testid="gallery-next" onClick={() => setActiveImage(i => (i + 1) % images.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full shadow"
                    style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}><ChevronRight size={18} /></button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button key={i} data-testid={`thumbnail-${i}`} onClick={() => setActiveImage(i)}
                    className="w-16 h-16 flex-shrink-0 border-2 overflow-hidden rounded transition-all"
                    style={{ borderColor: i === activeImage ? '#000' : 'rgba(0,0,0,0.06)', opacity: i === activeImage ? 1 : 0.5 }}>
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Info */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
            <h1 data-testid="product-title" className="font-sans text-2xl md:text-3xl font-bold mb-3" style={{ color: '#000' }}>
              {product.title}
            </h1>

            {/* Price */}
            <div className="flex items-center gap-3 mb-3">
              <span data-testid="product-price" className="font-sans text-2xl font-bold" style={{ color: '#000' }}>{formatPrice(currentPrice)}</span>
              {hasSale && (
                <>
                  <span className="font-sans text-base line-through" style={{ color: '#999' }}>{formatPrice(comparePrice)}</span>
                  <span className="font-sans text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: '#000', color: '#fff' }}>SAVE {savePct}%</span>
                </>
              )}
            </div>

            {/* Stock status */}
            <div className="mb-2">
              {inStock ? (
                <p className="font-sans text-xs font-medium" style={{ color: '#16a34a' }}>
                  <Check size={13} className="inline mr-1" />In Stock — Ships in 24-48h
                </p>
              ) : (
                <p className="font-sans text-xs font-medium" style={{ color: '#dc2626' }}>Out of Stock</p>
              )}
            </div>
            {lowStock && (
              <p className="font-sans text-xs font-medium mb-5" style={{ color: '#ea580c' }}>
                Only {product.stock_quantity} left in stock — order soon!
              </p>
            )}

            {/* Variants */}
            {product.options?.length > 0 && product.variants?.length > 0 && (
              <div className="mb-5">
                {product.options.map(opt => (
                  <div key={opt.name}>
                    <p className="font-sans text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#000' }}>
                      {opt.name} {selectedOptions[opt.name] && <span className="font-normal normal-case" style={{ color: '#666' }}>— {selectedOptions[opt.name]}</span>}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-1">
                      {opt.values.map(val => {
                        const isActive = selectedOptions[opt.name] === val;
                        return (
                          <button key={val} onClick={() => {
                            const newOpts = { ...selectedOptions, [opt.name]: val };
                            setSelectedOptions(newOpts);
                            setOptionErrors(prev => ({ ...prev, [opt.name]: false }));
                            const matchedVariant = product.variants.find(vr =>
                              product.options.every(o => newOpts[o.name] ? vr.options?.[o.name] === newOpts[o.name] : true)
                            );
                            if (matchedVariant && product.options.every(o => newOpts[o.name])) setSelectedVariant(matchedVariant);
                          }}
                            className="px-4 py-2 font-sans text-xs border transition-all rounded"
                            style={{
                              borderColor: optionErrors[opt.name] ? '#ef4444' : isActive ? '#000' : 'rgba(0,0,0,0.12)',
                              backgroundColor: isActive ? '#000' : '#fff',
                              color: isActive ? '#fff' : '#333',
                            }}>
                            {val}
                          </button>
                        );
                      })}
                    </div>
                    {optionErrors[opt.name] && (
                      <p className="font-sans text-xs mt-1 mb-2" style={{ color: '#ef4444' }}>Please select {opt.name.toLowerCase()}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Quantity + Add to Cart (FIX 5) */}
            <div className="flex gap-3 mb-4" ref={addToCartRef}>
              <div className="flex items-center border rounded-lg" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
                <button data-testid="qty-minus" onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="w-11 h-14 flex items-center justify-center hover:bg-[rgba(0,0,0,0.03)] transition-colors rounded-l-lg">
                  <Minus size={16} style={{ color: '#333' }} />
                </button>
                <span data-testid="qty-value" className="w-10 text-center font-sans text-sm font-semibold" style={{ color: '#000' }}>{quantity}</span>
                <button data-testid="qty-plus" onClick={() => setQuantity(q => q + 1)}
                  className="w-11 h-14 flex items-center justify-center hover:bg-[rgba(0,0,0,0.03)] transition-colors rounded-r-lg">
                  <Plus size={16} style={{ color: '#333' }} />
                </button>
              </div>
              <button data-testid="add-to-cart" onClick={handleAddToCart} disabled={!inStock || addingToCart}
                className={`flex-1 h-14 font-sans text-[15px] font-bold uppercase tracking-wider rounded-lg flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] ${shakeBtn ? 'animate-shake' : ''}`}
                style={{
                  background: !inStock ? '#ccc' : justAdded ? '#16a34a' : (hasVariantOptions && !allOptionsSelected) ? '#888' : '#000',
                  color: '#fff',
                  cursor: inStock && !addingToCart ? 'pointer' : 'not-allowed',
                }}>
                {addingToCart ? <Loader2 size={18} className="animate-spin" /> :
                 justAdded ? <><Check size={18} /> Added!</> :
                 !inStock ? 'Out of Stock' :
                 (hasVariantOptions && !allOptionsSelected) ? 'Select Options' :
                 <><ShoppingBag size={18} /> Add to Cart</>}
              </button>
            </div>

            {/* Payment Icons (FIX 2) */}
            <PaymentIcons size={36} className="mb-5" />

            {/* Trust Badges (FIX 6) */}
            <div className="grid grid-cols-2 gap-3 py-5 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
              <TrustBadge icon={Truck} title="FREE Shipping" desc="Free on all orders" bgColor="#16a34a" />
              <TrustBadge icon={RotateCcw} title="Easy Returns" desc="30-day hassle-free" bgColor="#3b82f6" />
              <TrustBadge icon={ShieldCheck} title="Secure Checkout" desc="100% secure payment" bgColor="#8b5cf6" />
              <TrustBadge icon={Award} title="Guaranteed" desc="Money-back guarantee" bgColor="#eab308" />
            </div>

            {/* Quantity Breaks (FIX 19) */}
            {qtyBreaks.length > 0 && qtyBreaks[0].quantity_breaks?.length > 0 && (
              <div className="mt-5 p-4 rounded-lg" style={{ backgroundColor: '#F9F9F7', border: '1px solid rgba(0,0,0,0.06)' }}>
                <h4 className="font-sans text-xs font-bold uppercase tracking-[0.15em] mb-3" style={{ color: '#000' }}>Buy More, Save More</h4>
                <div className="space-y-0">
                  <div className="grid grid-cols-3 py-2 border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                    <span className="font-sans text-[10px] uppercase tracking-wider" style={{ color: '#999' }}>Quantity</span>
                    <span className="font-sans text-[10px] uppercase tracking-wider" style={{ color: '#999' }}>Discount</span>
                    <span className="font-sans text-[10px] uppercase tracking-wider text-right" style={{ color: '#999' }}>Price Each</span>
                  </div>
                  <div className="grid grid-cols-3 py-2 border-b" style={{ borderColor: 'rgba(0,0,0,0.04)' }}>
                    <span className="font-sans text-sm" style={{ color: '#333' }}>1</span>
                    <span className="font-sans text-sm" style={{ color: '#999' }}>—</span>
                    <span className="font-sans text-sm text-right" style={{ color: '#000' }}>{formatPrice(currentPrice)}</span>
                  </div>
                  {qtyBreaks[0].quantity_breaks.map((qb, i) => {
                    const discounted = currentPrice * (1 - (qb.discount_value || 0) / 100);
                    const isBest = i === Math.floor(qtyBreaks[0].quantity_breaks.length / 2);
                    return (
                      <div key={i} className={`grid grid-cols-3 py-2 border-b ${isBest ? 'bg-[rgba(0,0,0,0.02)]' : ''}`} style={{ borderColor: 'rgba(0,0,0,0.04)' }}>
                        <span className="font-sans text-sm" style={{ color: '#333' }}>{qb.min_quantity}+</span>
                        <span className="font-sans text-sm font-medium" style={{ color: '#16a34a' }}>{qb.discount_value}% OFF</span>
                        <span className="font-sans text-sm font-medium text-right" style={{ color: '#000' }}>
                          {formatPrice(discounted)} {isBest && <span className="text-[10px] ml-1" style={{ color: '#eab308' }}>POPULAR</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Bundle Widget (FIX 3) */}
            {bundles.length > 0 && (
              <div className="mt-5">
                <BundleWidget bundles={bundles} formatPrice={formatPrice} />
              </div>
            )}
          </motion.div>
        </div>

        {/* Description (FIX 7) */}
        {product.description && (
          <div className="mt-14 max-w-3xl mx-auto">
            <div className="font-sans text-sm leading-relaxed
              [&_p]:mb-3 [&_p]:text-[#555] [&_p]:leading-relaxed
              [&_ul]:mb-3 [&_ul]:pl-5 [&_li]:mb-1.5 [&_li]:text-[#555]
              [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-[#000] [&_h2]:mb-3 [&_h2]:mt-8
              [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-[#000] [&_h3]:mb-2 [&_h3]:mt-6
              [&_strong]:text-[#000]
              [&_img]:w-full [&_img]:rounded-lg [&_img]:my-6
              [&_table]:w-full [&_table]:border-collapse [&_table]:my-4
              [&_th]:text-left [&_th]:p-2.5 [&_th]:bg-[rgba(0,0,0,0.03)] [&_th]:font-sans [&_th]:text-xs [&_th]:font-bold [&_th]:text-[#000] [&_th]:border-b [&_th]:border-[rgba(0,0,0,0.08)]
              [&_td]:p-2.5 [&_td]:text-sm [&_td]:border-b [&_td]:border-[rgba(0,0,0,0.04)]
              [&_.benefits-grid]:grid [&_.benefits-grid]:grid-cols-2 [&_.benefits-grid]:gap-4 [&_.benefits-grid]:my-6
              [&_.feature-section]:flex [&_.feature-section]:gap-6 [&_.feature-section]:items-center [&_.feature-section]:my-6"
              dangerouslySetInnerHTML={{ __html: product.description }} />
          </div>
        )}

        {/* Accordions */}
        <div className="mt-10 max-w-3xl mx-auto">
          <AccordionItem title="Specifications">
            <ul className="space-y-1.5">
              {product.sku && <li>SKU: {product.sku}</li>}
              {product.barcode_gtin && <li>Barcode: {product.barcode_gtin}</li>}
              {product.weight && <li>Weight: {product.weight}g</li>}
              <li>Condition: {product.condition || 'New'}</li>
              <li>Category: {product.category || 'General'}</li>
            </ul>
          </AccordionItem>
          <AccordionItem title="Shipping & Delivery">
            <p>Free shipping on all orders. Delivery in 3-7 business days. Express shipping available at checkout for faster delivery.</p>
          </AccordionItem>
          <AccordionItem title="Our Guarantee">
            <p>30-day money-back guarantee. If you're not completely satisfied, return the product in its original condition for a full refund. No questions asked.</p>
          </AccordionItem>
        </div>

        {/* Reviews */}
        <div id="reviews-section" className="mt-14 max-w-3xl mx-auto scroll-mt-20">
          <h2 className="font-sans text-lg font-bold mb-6" style={{ color: '#000' }}>Customer Reviews</h2>
          <div className="text-center py-10 border rounded-lg" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            <p className="font-sans text-sm mb-1" style={{ color: '#666' }}>No reviews yet</p>
            <p className="font-sans text-xs" style={{ color: '#999' }}>Be the first to share your experience with this product.</p>
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="font-serif text-2xl tracking-tight text-center mb-8" style={{ color: '#000' }}>You May Also Like</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map(p => <ProductCard key={p.handle} product={p} formatPrice={formatPrice} />)}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Add to Cart (FIX 1 — IntersectionObserver, both mobile+desktop) */}
      <AnimatePresence>
        {showSticky && (
          <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} transition={{ duration: 0.25, ease: 'easeOut' }}
            data-testid="sticky-add-to-cart"
            className="fixed bottom-0 left-0 right-0 z-50 py-2.5 px-4 border-t shadow-lg"
            style={{ backgroundColor: '#fff', borderColor: 'rgba(0,0,0,0.06)' }}>
            <div className="max-w-7xl mx-auto flex items-center gap-3">
              {/* Thumbnail — desktop */}
              {images[0] && (
                <div className="hidden sm:block w-10 h-10 rounded overflow-hidden flex-shrink-0" style={{ backgroundColor: '#f5f3ef' }}>
                  <img src={images[0].url} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-sans text-xs truncate" style={{ color: '#333' }}>{product.title}</p>
                <p className="font-sans text-sm font-bold" style={{ color: '#000' }}>{formatPrice(currentPrice)}</p>
              </div>
              {/* Quantity — desktop */}
              <div className="hidden sm:flex items-center border rounded" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-8 h-9 flex items-center justify-center"><Minus size={14} /></button>
                <span className="w-8 text-center font-sans text-xs font-semibold">{quantity}</span>
                <button onClick={() => setQuantity(q => q + 1)} className="w-8 h-9 flex items-center justify-center"><Plus size={14} /></button>
              </div>
              <button onClick={handleAddToCart} disabled={!inStock || addingToCart}
                className="px-6 sm:px-8 h-10 font-sans text-xs font-bold uppercase tracking-wider rounded-lg flex items-center gap-2 transition-all active:scale-[0.98]"
                style={{ background: justAdded ? '#16a34a' : (hasVariantOptions && !allOptionsSelected) ? '#888' : '#000', color: '#fff' }}>
                {addingToCart ? <Loader2 size={14} className="animate-spin" /> :
                 justAdded ? <><Check size={14} /> Added</> :
                 (hasVariantOptions && !allOptionsSelected) ? 'Select Options' :
                 <><ShoppingBag size={14} /> Add to Cart</>}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
